import { useEffect, useState } from "react";
import { encodeArtwork } from "@/shared/utils/artworkTransport";
import { EmailTestCard } from "./EmailTestCard";
import { SmsTestCard } from "./SmsTestCard";
import { Alert, Badge, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import {
  DeliveryService,
  type Suppression,
  type PostalAddress,
  type ProviderAccount,
  type TestSendResult,
  type VerifiedAddress,
} from "@services/delivery/deliveryService";

interface DeliverySetupTabProps {
  organizationId: number;
}

const EMPTY_ADDRESS: PostalAddress = {
  name: "",
  address_line1: "",
  address_line2: "",
  address_city: "",
  address_state: "",
  address_zip: "",
};

const SAMPLE_FRONT =
  '<html><body style="margin:0;font-family:Helvetica,Arial,sans-serif">' +
  '<div style="padding:0.5in;background:#5b3df5;color:#fff;height:5.25in">' +
  '<div style="font-size:30pt;font-weight:700;margin-top:1.4in">Hello {{first_name}}</div>' +
  "</div></body></html>";
const SAMPLE_BACK =
  '<html><body style="margin:0;font-family:Helvetica,Arial,sans-serif">' +
  '<div style="padding:0.5in;font-size:12pt">Scan the code to get started.</div>' +
  "</body></html>";

export const DeliverySetupTab = ({ organizationId }: DeliverySetupTabProps) => {
  const [accounts, setAccounts] = useState<ProviderAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  const service = new DeliveryService();

  const load = () => {
    setLoading(true);
    service
      .listAccounts(organizationId)
      .then(setAccounts)
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load delivery accounts."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [organizationId]);


  if (loading) {
    return <Spinner animation="border" />;
  }

  return (
    <div>
      <div className="mb-4">
        <h5 className="mb-1">Direct Mail</h5>
        <p className="text-muted mb-0">
          Mail is sent one piece per person, so this organization&rsquo;s account is what pays for
          the postage and what the return address belongs to.
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>Accounts</Card.Header>
        <Card.Body className="p-0">
          <Table className="mb-0" responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Provider</th>
                <th>Key</th>
                <th>Webhook</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted text-center py-4">
                    No delivery account yet. Add one below to send mail for this organization.
                  </td>
                </tr>
              )}
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>{account.name}</td>
                  <td className="text-capitalize">{account.provider}</td>
                  <td>
                    {/* Which environment a key points at is the question people ask, and the
                        only safe half of the answer to show. */}
                    {account.key_environment === "live" ? (
                      <Badge bg="danger">Live &mdash; sends real mail</Badge>
                    ) : account.key_environment === "test" ? (
                      <Badge bg="secondary">Test &mdash; nothing is posted</Badge>
                    ) : (
                      <Badge bg="warning" text="dark">
                        No key
                      </Badge>
                    )}
                    {/* Which key is in play matters when one client's mail behaves
                        differently from everyone else's, and it is the first thing to check. */}
                    {account.key_environment && (
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {account.uses_own_key ? "this client's own key" : "Motimatic account"}
                      </div>
                    )}
                  </td>
                  <td>
                    {/* Until this is connected Lob sends us nothing, and a campaign reports
                        pieces posted followed by permanent zeroes for delivered and scanned.
                        It used to need an engineer with AWS access, which put the step the
                        whole channel depends on outside the reach of whoever set the client
                        up. */}
                    <WebhookCell account={account} onChanged={load} />
                  </td>
                  <td>{!account.is_active && <Badge bg="secondary">Inactive</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* The three accounts create themselves when this page loads, using Motimatic's own
          Lob, SES and Twilio credentials from settings. Nearly every client is on those, so
          there is nothing here to fill in and no key to paste.

          Bringing a client's own account is real but rare, and it is a conversation with
          engineering rather than a form: it needs a key pair, its own carrier registration on
          SMS, and its own sending domain on email. Putting an API key box on the page made
          that rare case look like the normal one, and cost Rachel twenty minutes wondering
          which key she was supposed to have. */}
      <Card className="mb-4">
        <Card.Body className="text-muted small py-3">
          These accounts use Motimatic&apos;s own Lob, SES and Twilio credentials and set
          themselves up. There is nothing to configure here. A client bringing their own
          provider account is handled by engineering.
        </Card.Body>
      </Card>

      <EmailTestCard accounts={accounts} />

      <SmsTestCard organizationId={organizationId} />

      <SuppressionCard organizationId={organizationId} />

      {accounts.length > 0 && <TestSendCard accounts={accounts} />}
    </div>
  );
};

/*
    Connecting an account's events.

    Lob only sends delivery, return and scan events to a registered webhook, so until this is
    done a campaign shows pieces posted and then nothing else, forever. That is the worst kind
    of broken: it looks like it shipped.

    Reconnecting is deliberately a second click behind a confirm. It deletes the subscription
    at Lob and makes a new one, which is correct after a key rotation and pointless otherwise.
*/
const WebhookCell = ({
  account,
  onChanged,
}: {
  account: ProviderAccount;
  onChanged: () => void;
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = async (replace: boolean) => {
    if (replace && !window.confirm(
      "Reconnecting replaces this account's webhook at Lob. Only do this after changing the " +
      "API key. Continue?",
    )) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await new DeliveryService().connectWebhook(account.id, replace);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not connect the webhook.");
    } finally {
      setBusy(false);
    }
  };

  if (busy) return <Spinner size="sm" animation="border" />;

  return (
    <>
      {account.webhook_configured ? (
        <>
          <Badge bg="success">Connected</Badge>
          <Button variant="link" size="sm" className="p-0 ms-2" onClick={() => connect(true)}>
            Reconnect
          </Button>
        </>
      ) : (
        <>
          <Button size="sm" variant="outline-primary" onClick={() => connect(false)}>
            Connect
          </Button>
          <div className="text-muted" style={{ fontSize: 11 }}>
            Needed before any delivery or scan is reported
          </div>
        </>
      )}
      {error && <div className="text-danger" style={{ fontSize: 11 }}>{error}</div>}
    </>
  );
};

/*
    The do-not-contact list, across all three channels.

    It has to be something an operator can add to the moment a phone call ends, because every
    channel has a version of ignoring the request that is worse than the last: a postcard
    arrives at somebody's house with a client's name on it, an email damages a sending
    domain, and a text to somebody who opted out is priced per message by a regulator.

    Which identifier you give decides how far it reaches. An address stops mail only, because
    an address is a household rather than a person, and somebody opting out of marketing email
    has not asked the university to stop posting things. An email or a phone number stops both
    email and SMS, because those identify a person, and a person asking to be left alone means
    on every channel they can be reached rather than the one they happened to use to ask.

    Stored as a one-way hash, never the value. The entry can say a request was recorded
    without the platform keeping a second copy of somebody's home address or mobile number,
    which is why the table shows a reason and a date and no identifier at all.
*/
const REASON_LABEL: Record<string, string> = {
  requested: "They asked us to stop",
  returned: "Mail came back",
  undeliverable: "Address not recognised",
  manual: "Added by hand",
  unsubscribed: "They unsubscribed",
  bounced: "Email bounced",
  complained: "Marked as spam",
};

type SuppressBy = "address" | "email" | "phone";

const CHANNEL_LABEL: Record<string, string> = {
  direct_mail: "Mail",
  email: "Email",
  sms: "SMS",
};

const SuppressionCard = ({ organizationId }: { organizationId: number }) => {
  const [rows, setRows] = useState<Suppression[]>([]);
  const [by, setBy] = useState<SuppressBy>("address");
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState({
    address_line1: "",
    address_line2: "",
    address_city: "",
    address_state: "",
    address_zip: "",
  });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = new DeliveryService();
  const load = () => {
    service.listSuppressions(organizationId).then(setRows).catch(() => setRows([]));
  };
  useEffect(load, [organizationId]);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await service.addSuppression({
        organization_id: organizationId,
        // Exactly one, which is what the server expects: the identifier chooses the channel.
        ...(by === "address" ? { address } : by === "email"
          ? { email: contact.trim() }
          : { phone: contact.trim() }),
        note,
      });
      setAddress({ address_line1: "", address_line2: "", address_city: "", address_state: "", address_zip: "" });
      setContact("");
      setNote("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add that address.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: Suppression) => {
    await service.removeSuppression(row.id);
    load();
  };

  const field = (key: keyof typeof address, label: string, width = 180) => (
    <Form.Group controlId={`suppress-${key}`} style={{ width }}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        value={address[key]}
        onChange={(e) => setAddress({ ...address, [key]: e.target.value })}
      />
    </Form.Group>
  );

  return (
    <Card className="mb-4">
      <Card.Header>Do not contact</Card.Header>
      <Card.Body>
        <p className="text-muted small">
          People and addresses this client must never contact again. Applies to every campaign
          they run and survives a data file being reloaded. Mail that comes back, email that
          bounces and anyone who texts STOP are added automatically; add one by hand when
          somebody asks us directly.
        </p>

        <div className="d-flex gap-3 align-items-end flex-wrap mb-3">
          <Form.Group controlId="suppress-by" style={{ minWidth: 190 }}>
            <Form.Label>Stop contacting</Form.Label>
            <Form.Select value={by} onChange={(e) => setBy(e.target.value as SuppressBy)}>
              <option value="address">A postal address</option>
              <option value="email">An email address</option>
              <option value="phone">A mobile number</option>
            </Form.Select>
          </Form.Group>

          {by === "address" ? (
            <>
              {field("address_line1", "Address", 240)}
              {field("address_line2", "Apt / Suite", 130)}
              {field("address_city", "City")}
              {field("address_state", "State", 90)}
              {field("address_zip", "ZIP", 110)}
            </>
          ) : (
            <Form.Group controlId="suppress-contact" style={{ minWidth: 260 }}>
              <Form.Label>{by === "email" ? "Email address" : "Mobile number"}</Form.Label>
              <Form.Control
                value={contact}
                placeholder={by === "email" ? "someone@example.com" : "+1 202 555 0123"}
                onChange={(e) => setContact(e.target.value)}
              />
              <Form.Text className="text-muted">
                Stops email and texts both. Somebody asking to be left alone means on every
                channel, not the one they happened to use to ask.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group controlId="suppress-note" style={{ width: 220 }}>
            <Form.Label>Note</Form.Label>
            <Form.Control value={note} onChange={(e) => setNote(e.target.value)} />
          </Form.Group>
          <Button
            onClick={add}
            disabled={
              busy ||
              (by === "address"
                ? !address.address_line1 || !address.address_zip
                : contact.trim().length === 0)
            }
          >
            {busy ? <Spinner size="sm" animation="border" /> : "Add"}
          </Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Table responsive className="align-middle mb-0">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Reason</th>
              <th>Note</th>
              <th>Added</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted text-center py-4">
                  Nobody has asked this client to stop contacting them.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="small">{CHANNEL_LABEL[row.channel] ?? row.channel}</td>
                <td>{REASON_LABEL[row.reason] ?? row.reason}</td>
                <td className="text-muted small">{row.note}</td>
                <td className="text-muted small">{row.created_on}</td>
                <td className="text-end">
                  <Button size="sm" variant="link" onClick={() => remove(row)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {rows.length > 0 && (
          <div className="text-muted mt-2" style={{ fontSize: 11 }}>
            The address or number itself is not stored, only a one-way hash of it, so it can
            be matched against a file without being read back.
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const TestSendCard = ({ accounts }: { accounts: ProviderAccount[] }) => {
  const [accountId, setAccountId] = useState(accounts[0].id);
  const [to, setTo] = useState<PostalAddress>(EMPTY_ADDRESS);
  const [live, setLive] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<TestSendResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
      The verdict on what has been typed so far.

      Checked before anything is sent, because verification neither prints nor posts and the
      moment it is useful is while somebody can still fix a typo. Finding out afterwards, from
      a card that came back a week later, is the problem this screen exists to remove.
  */
  const [verified, setVerified] = useState<VerifiedAddress | null>(null);
  const [verifying, setVerifying] = useState(false);

  const enoughToCheck = !!to.address_line1 && (!!to.address_zip || !!to.address_city);

  const check = async () => {
    if (!enoughToCheck) return;
    setVerifying(true);
    try {
      setVerified(await new DeliveryService().verifyAddress(accountId, to));
    } catch {
      // A verification that failed to run says nothing about the address, so it says
      // nothing at all rather than showing a verdict nobody can trust.
      setVerified(null);
    } finally {
      setVerifying(false);
    }
  };

  /** Take the postal service's spelling, rather than making somebody retype it. */
  const acceptStandardized = () => {
    if (!verified?.components) return;
    setTo({ ...to, ...verified.components });
    setVerified({ ...verified, standardized: verified.standardized });
  };

  const field = (key: keyof PostalAddress, label: string, width = 200) => (
    <Form.Group controlId={`delivery-test-${key}`} style={{ minWidth: width }}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        value={to[key] ?? ""}
        onChange={(e) => {
          setTo({ ...to, [key]: e.target.value });
          // The old verdict describes an address that no longer exists on screen.
          setVerified(null);
        }}
        onBlur={check}
      />
    </Form.Group>
  );

  const send = async () => {
    if (live && !window.confirm(
      "This prints and posts a real postcard, and charges your Lob account. Continue?",
    )) {
      return;
    }
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const response = await new DeliveryService().testSend(accountId, {
        to,
        // Encoded, because the sample artwork is styled and a WAF rule rejects CSS in a
        // request body before it reaches the API (see artworkTransport).
        piece: { size: "4x6", front_b64: encodeArtwork(SAMPLE_FRONT),
                 back_b64: encodeArtwork(SAMPLE_BACK),
                 merge_variables: { first_name: to.name.split(" ")[0] || "there" } },
        live,
      });
      setResult(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The test send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <Card.Header>Send a test card</Card.Header>
      <Card.Body>
        <p className="text-muted">
          Checks the address against the postal service, prices the card on this
          account&rsquo;s plan, and renders the real PDF the printer would produce. Nothing is
          posted unless you tick the box.
        </p>

        <div className="d-flex gap-3 flex-wrap mb-3">
          <Form.Group controlId="delivery-test-account" style={{ minWidth: 200 }}>
            <Form.Label>Account</Form.Label>
            <Form.Select value={accountId} onChange={(e) => setAccountId(Number(e.target.value))}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.key_environment ?? "no key"})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {field("name", "Recipient")}
          {field("address_line1", "Address", 260)}
          {field("address_line2", "Apt / Suite", 140)}
          {field("address_city", "City")}
          {field("address_state", "State", 100)}
          {field("address_zip", "ZIP", 120)}
        </div>

        <div className="d-flex align-items-center gap-3">
          <Form.Check
            type="switch"
            id="delivery-test-live"
            label="Send for real (prints, posts and charges)"
            checked={live}
            onChange={(e) => setLive(e.target.checked)}
          />
          <Button variant={live ? "danger" : "primary"} onClick={send} disabled={sending || !to.address_line1}>
            {sending ? <Spinner size="sm" animation="border" /> : live ? "Send real card" : "Render test card"}
          </Button>
        </div>

        {(verifying || verified) && !result && (
          <AddressVerdict
            verified={verified}
            verifying={verifying}
            onAccept={acceptStandardized}
            typed={to}
          />
        )}

        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
        {result && <TestSendOutcome result={result} />}
      </Card.Body>
    </Card>
  );
};

/*
    What the postal service makes of the address, while it can still be corrected.

    Three states, and the middle one is the reason this exists. Deliverable and undeliverable
    are obvious. `risky` means the building is real and the unit may not be, so the card
    arrives at a lobby rather than a person and nothing else about the send looks wrong -- it
    is the failure that costs money quietly, so it is shown in its own colour rather than
    folded in with "deliverable".

    "Not checked" is stated plainly rather than dressed as a pass. An account with no live key
    cannot get a verdict, and a green tick nobody earned is worse than no tick.
*/
const AddressVerdict = ({
  verified, verifying, onAccept, typed,
}: {
  verified: VerifiedAddress | null;
  verifying: boolean;
  onAccept: () => void;
  typed: PostalAddress;
}) => {
  if (verifying) {
    return (
      <div className="mt-3 small text-muted d-flex align-items-center gap-2">
        <Spinner size="sm" animation="border" /> Checking the address…
      </div>
    );
  }
  if (!verified) return null;

  const tone = !verified.checked
    ? "secondary"
    : verified.risky
      ? "warning"
      : verified.deliverability === "deliverable"
        ? "success"
        : "danger";

  // Only worth offering when it actually differs from what is on screen.
  const differs =
    !!verified.components &&
    (Object.keys(verified.components) as (keyof PostalAddress)[]).some(
      (key) => (verified.components?.[key] ?? "").toUpperCase()
        !== (typed[key] ?? "").toUpperCase(),
    );

  return (
    <Alert variant={tone} className="mt-3 mb-0 py-2">
      <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
        <div>
          <div className="fw-semibold">
            {verified.checked ? verified.standardized ?? verified.deliverability : "Not checked"}
          </div>
          <div className="small">{verified.note}</div>
        </div>
        {verified.checked && differs && (
          <Button size="sm" variant={`outline-${tone}`} onClick={onAccept}>
            Use the corrected address
          </Button>
        )}
      </div>
    </Alert>
  );
};

const TestSendOutcome = ({ result }: { result: TestSendResult }) => (
  <div className="mt-4">
    <div className="d-flex gap-4 flex-wrap">
      <div>
        <div className="text-muted small">Address</div>
        {result.address.checked ? (
          <>
            <div className="fw-semibold">{result.address.standardized}</div>
            <Badge bg={result.address.deliverability === "deliverable" && !result.address.risky
              ? "success" : result.address.risky ? "warning" : "danger"}
              text={result.address.risky ? "dark" : undefined}>
              {result.address.deliverability}
            </Badge>
          </>
        ) : (
          <div className="fw-semibold text-muted">Not checked</div>
        )}
        <div className="small text-muted mt-1">{result.address.note}</div>
      </div>

      <div>
        <div className="text-muted small">Cost per card</div>
        <div className="fw-semibold">${result.cost.per_piece}</div>
        <div className="small text-muted">
          {result.cost.source}
          {result.cost.charged ? " · charged" : " · nothing charged"}
        </div>
      </div>

      <div>
        <div className="text-muted small">Proof</div>
        <a href={result.proof.pdf_url} target="_blank" rel="noreferrer" className="fw-semibold">
          Open the PDF
        </a>
        <div className="small text-muted">
          {result.proof.mailed
            ? `Posted. Expected delivery ${result.proof.expected_delivery_date ?? "unknown"}.`
            : "Rendered only. Nothing was posted."}
        </div>
      </div>
    </div>

    {/*
        The card itself, not a link to it.

        This is the printer's own PDF -- both sides, fonts resolved, the address block and
        barcode where Lob actually puts them -- and it is the only artefact in the product
        that tells the truth about what arrives. Behind a link it went unopened, which meant
        the one check worth doing was the one being skipped.
    */}
    {result.proof.pdf_url && (
      <div className="mt-3">
        <object
          data={`${result.proof.pdf_url}#toolbar=0&navpanes=0`}
          type="application/pdf"
          width="100%"
          height="520"
          aria-label="The rendered postcard"
          style={{ border: "1px solid #e2e8f0", borderRadius: 6, background: "#f8fafc" }}
        >
          {/* Some browsers refuse to embed a PDF. The link is the fallback, not the
              default. */}
          <div className="p-3 small text-muted">
            This browser will not display the PDF inline.{" "}
            <a href={result.proof.pdf_url} target="_blank" rel="noreferrer">
              Open it in a new tab
            </a>
            .
          </div>
        </object>
      </div>
    )}
  </div>
);
